/**
 * The header, summary strip, key and footer for the org chart PDF export.
 *
 * BALKAN's exporter takes `header` and `footer` as arbitrary HTML strings and
 * wraps each in an absolutely-positioned div on every page, substituting
 * `{current-page}` and `{total-pages}` — so the whole banner repeats across a
 * multi-page export for free.
 *
 * Two constraints shape what is written here:
 *
 *  - Every rule is an inline style. The exported document is assembled by
 *    BALKAN (in an iframe for the preview, on their render service for the
 *    file itself) and carries none of the app's CSS, so a class name would
 *    resolve to nothing. Layout uses `display:table` rather than flexbox for
 *    the same reason — it survives the widest range of renderers.
 *  - The header is drawn inside the page's TOP MARGIN band and bottom-anchored
 *    within it. Anything taller than `margin[0]` is clipped off the top of the
 *    page, so HEADER_BAND_HEIGHT below is the measured height of this markup
 *    plus headroom, and is the value callers must pass as `margin[0]`.
 */

import {
    ORGANIGRAM_COMMITTED_COLOUR,
    ORGANIGRAM_TAG_COLOURS,
    ORGANIGRAM_UNTYPED_TAG,
    POSITION_TYPE_ORDER,
    TYPE_TO_ORGANIGRAM_TAG,
    organigramBehindScheduleGlyph,
    positionTypeLabel,
} from 'Components/people/positionTypes';
import { organigramLegendContents, organigramMetrics } from 'Components/workforce/organigramMetrics';

import { getIntlLocale } from 'Helpers/localeService';

/**
 * Top page margin, in points, that the header markup needs. Title block, the
 * metric tiles and the key measure ~150pt together; the exporter leaves a 7pt
 * gap below the header, and the rest is headroom for a scope name that wraps.
 */
export const HEADER_BAND_HEIGHT = 190;

/** Positions, Layers, Average span, Maximum span, FTE — one row, equal widths. */
const METRIC_TILE_COUNT = 5;

const COLOUR_TEXT = '#1B1C1D'; // charcoal.DEFAULT
const COLOUR_MUTED = '#545556'; // charcoal.75
const COLOUR_FAINT = '#8D8E8E'; // charcoal.50
const COLOUR_RULE = '#E4E4E4'; // grey
const COLOUR_TILE = '#F6F6F6'; // charcoal.10
const COLOUR_ACCENT = '#00DD8D'; // green.DEFAULT — the wordmark's leading dots

const FONT_STACK = 'Arial, Helvetica, sans-serif';

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * The zone the export's dates are read in. Nothing publishes the user's
 * configured zone to the front end, so the browser's own zone is the only one
 * available; UTC covers a runtime that reports none.
 */
function exportTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/** "24 August 2026", in the user's locale and the browser's timezone. */
function exportDate(date = new Date()) {
    return new Intl.DateTimeFormat(getIntlLocale(), {
        timeZone: exportTimezone(),
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }).format(date);
}

/** The parts of the header's meta line that are actually known, joined with a middot. */
function joinParts(parts) {
    return parts.filter((part) => part !== null && part !== undefined && String(part).trim() !== '').join(' · ');
}

function metricTile(label, value, isLast) {
    return (
        `<div style="display:table-cell;width:${100 / METRIC_TILE_COUNT}%;padding-${isLast ? 'right:0' : 'right:8px'};vertical-align:top;">` +
        `<div style="background:${COLOUR_TILE};border:1px solid ${COLOUR_RULE};border-radius:8px;padding:8px 10px;">` +
        `<div style="font-size:11px;color:${COLOUR_MUTED};font-weight:500;">${escapeHtml(label)}</div>` +
        `<div style="font-size:19px;line-height:22px;font-weight:600;color:${COLOUR_TEXT};">${escapeHtml(value)}</div>` +
        '</div></div>'
    );
}

function keySwatch(colour, label) {
    return (
        `<span style="margin-right:16px;white-space:nowrap;">` +
        `<span style="display:inline-block;width:11px;height:11px;border-radius:3px;background:${colour};vertical-align:-1px;margin-right:5px;"></span>` +
        `${escapeHtml(label)}</span>`
    );
}

/**
 * The key, listing only what the exported chart contains. A chart filtered down
 * to volunteers explains one colour, not four.
 */
function keyRow(nodes) {
    const legend = organigramLegendContents(nodes, POSITION_TYPE_ORDER);

    const entries = legend.types.map((type) => keySwatch(ORGANIGRAM_TAG_COLOURS[TYPE_TO_ORGANIGRAM_TAG[type]], positionTypeLabel(type)));

    if (legend.untyped) {
        entries.push(keySwatch(ORGANIGRAM_TAG_COLOURS[ORGANIGRAM_UNTYPED_TAG], $t('Untyped')));
    }

    if (legend.committed) {
        entries.push(
            `<span style="margin-right:16px;white-space:nowrap;">` +
                `<span style="display:inline-block;width:9px;height:9px;border-radius:50%;background:${ORGANIGRAM_COMMITTED_COLOUR};margin-right:5px;"></span>` +
                `${escapeHtml($t('Committed'))}</span>`,
        );
    }

    // The same glyph the card draws, from the same source, so the key explains
    // the mark rather than merely resembling it. The size has to be a CSS rule,
    // not just width/height attributes: an unsized inline SVG falls back to the
    // 300×150 default and pushes the rest of the key onto its own line.
    if (legend.behindSchedule) {
        entries.push(
            `<span style="white-space:nowrap;">` +
                `<svg viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg" style="display:inline-block;width:10px;height:10px;vertical-align:-1px;margin-right:5px;">${organigramBehindScheduleGlyph()}</svg>` +
                `${escapeHtml($t('Behind schedule'))}</span>`,
        );
    }

    if (entries.length === 0) {
        return '';
    }

    return (
        `<div style="margin-top:9px;font-size:11px;line-height:14px;color:#3A3B3C;">` +
        `<span style="font-weight:600;color:${COLOUR_TEXT};margin-right:14px;">${escapeHtml($t('Key'))}</span>` +
        entries.join('') +
        '</div>'
    );
}

/**
 * @param {Array}  nodes    The flat node array the chart was given.
 * @param {Object} context  { scope, project, budgetVersion }
 */
export function organigramPdfHeader(nodes, context = {}) {
    const metrics = organigramMetrics(nodes);
    const meta = joinParts([context.project, context.budgetVersion ? `${$t('Budget version')} ${context.budgetVersion}` : null]);

    return (
        `<div style="font-family:${FONT_STACK};color:${COLOUR_TEXT};">` +
        // Title block: scope on the left, wordmark and export date on the right.
        `<div style="display:table;width:100%;border-bottom:2px solid ${COLOUR_TEXT};padding-bottom:10px;">` +
        `<div style="display:table-cell;vertical-align:bottom;">` +
        `<div style="font-size:19px;line-height:23px;font-weight:600;">${escapeHtml(context.scope || $t('Organisational chart'))}</div>` +
        (meta ? `<div style="font-size:12px;line-height:15px;color:${COLOUR_MUTED};margin-top:3px;">${escapeHtml(meta)}</div>` : '') +
        '</div>' +
        `<div style="display:table-cell;vertical-align:bottom;text-align:right;white-space:nowrap;">` +
        `<div style="font-size:18px;line-height:21px;font-weight:700;letter-spacing:-0.5px;"><span style="color:${COLOUR_ACCENT};">..</span>inbudget</div>` +
        `<div style="font-size:11px;line-height:14px;color:${COLOUR_FAINT};margin-top:2px;">${escapeHtml($t('Exported'))} ${escapeHtml(exportDate())}</div>` +
        '</div></div>' +
        // Summary strip.
        `<div style="display:table;table-layout:fixed;width:100%;margin-top:10px;">` +
        '<div style="display:table-row;">' +
        metricTile($t('Positions'), metrics.positions, false) +
        metricTile($t('Layers'), metrics.layers, false) +
        metricTile($t('Average span'), metrics.averageSpan, false) +
        metricTile($t('Maximum span'), metrics.maximumSpan, false) +
        metricTile($t('FTE'), metrics.fte, true) +
        '</div></div>' +
        keyRow(nodes) +
        '</div>'
    );
}

/** Breadcrumb on the left, page counter on the right. */
export function organigramPdfFooter(context = {}) {
    const breadcrumb = joinParts(['InBudget', context.project, context.scope]);

    return (
        `<div style="font-family:${FONT_STACK};font-size:11px;line-height:14px;color:${COLOUR_FAINT};display:table;width:100%;border-top:1px solid ${COLOUR_RULE};padding-top:8px;">` +
        `<div style="display:table-cell;">${escapeHtml(breadcrumb)}</div>` +
        // The braced tokens are substituted per page by the exporter, so they
        // pass through the translation as ordinary interpolated values.
        `<div style="display:table-cell;text-align:right;white-space:nowrap;">` +
        escapeHtml($t('Page {{current}} of {{total}}', { current: '{current-page}', total: '{total-pages}' })) +
        '</div>' +
        '</div>'
    );
}

/**
 * Options for `OrgChart.pdfPrevUI.show()`.
 *
 * `margin` is set HERE rather than in an `onExportStart` handler on purpose:
 * `_export()` computes the page split from `options.margin` BEFORE it publishes
 * `exportstart`, so a margin raised inside that handler would leave the pages
 * sized for the old, shorter top band and the chart would run under the header.
 *
 * @param {Array}  nodes    The flat node array the chart was given.
 * @param {Object} context  { scope, project, budgetVersion }
 */
export function organigramPdfOptions(nodes, context = {}) {
    // Format, orientation and scale keep the values the export already shipped
    // with; the preview dialog still lets the reader change all three.
    return {
        format: 'A4',
        landscape: true,
        scale: 100,
        margin: [HEADER_BAND_HEIGHT, 40, 50, 40],
        header: organigramPdfHeader(nodes, context),
        footer: organigramPdfFooter(context),
        filename: `${organigramPdfFilename(context)}.pdf`,
    };
}

/** A filename the reader can recognise in their downloads folder. */
function organigramPdfFilename(context) {
    const stem = joinParts([context.project, context.scope]) || 'organisational-chart';

    return stem
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
}
